














































































import { Vue, Component, Prop } from 'vue-property-decorator';
import elementResizeDetectorMaker from 'element-resize-detector';
import debounce from 'debounce';
import { isPageInFocus } from '@signing-page/tracking/helpers/tracking.helper';
import { IManuallySignedPage } from '@signing-page/documents/types/manually-signed-page';
import { EDocumentStatus } from '@signing-page/documents/types/enums/document-status';
import { EDocumentPageType } from '@/enums/document-page-type';
import DocumentHeader from '@signing-page/documents/components/DocumentHeader.vue';
import ContentBlockContainer from '@signing-page/documents/components/ContentBlockContainer.vue';
import DocumentAttachments from '@signing-page/documents/components/DocumentAttachments.vue';
import { IDocument } from '@signing-page/documents/types/document';
import { IRecipient } from '@signing-page/documents/types/recipient';
import { IDocumentPage } from '@/types/document-page';
import { IPageElement } from '@signing-page/signing/types/page-element';
import { ITheme } from '@signing-page/entity/types/theme';
import { IMergeValues } from '@/editor/types/merge-values';
import { IDocumentAttachment } from '@/types/document-attachment';
import { IEntity } from '@signing-page/entity/types/entity';

@Component({
  components: {
    DocumentHeader,
    ContentBlockContainer,
    DocumentAttachments,
  },
})
export default class DocumentContainer extends Vue {
  @Prop() document!: IDocument;
  @Prop() documentPages!: IDocumentPage[];
  @Prop() manuallySignedPages: IManuallySignedPage[];
  @Prop() pageWidth: number;
  @Prop() recipient: IRecipient;
  @Prop() entity: IEntity;
  @Prop() theme: ITheme;
  @Prop() videoThumbnail?: string;
  @Prop() videoUrl?: string;
  @Prop() reminderExpirationDays: number;
  @Prop() focusedRequiredId?: string;
  @Prop() currentDocumentPage?: IDocumentPage;
  @Prop() uploadingAttachmentIds: string[];
  @Prop() isPreview!: boolean;
  @Prop() requiredStepsLeft!: boolean;
  @Prop() attachments: IDocumentAttachment[];
  @Prop() emptyBottomBar: boolean;
  @Prop() initials: string;
  @Prop() verifiedInitialFieldIds: string[];

  elementResizeDetector = elementResizeDetectorMaker();
  scrollMargin = 20;
  hasScrolledToBottom = false;

  get isSigned(): boolean {
    return this.document.status === EDocumentStatus.Signed;
  }

  get hasAttachments(): boolean {
    return !!this.attachments.length;
  }

  get allRecipients(): IRecipient[] {
    return this.document.recipients;
  }

  get firstPage(): IDocumentPage {
    return !!this.documentPages && this.document.pages.length > 0 && this.documentPages[0];
  }

  get visibleDocumentContentPageElements(): IPageElement[] {
    const contentBlockContainers: ContentBlockContainer[] = this.$refs
      .pages as ContentBlockContainer[];

    const filteredTypes: EDocumentPageType[] = [
      EDocumentPageType.DocumentContent,
      EDocumentPageType.ContentLink,
      EDocumentPageType.Editor,
    ];

    return contentBlockContainers
      .filter(
        ({ page: { pageType }, isVisible }: ContentBlockContainer) =>
          filteredTypes.includes(pageType) && isVisible
      )
      .map(({ page: { pageType, id }, $refs }: ContentBlockContainer) => ({
        id,
        element: $refs.page as HTMLElement,
        type: pageType,
      }));
  }

  get documentManuallySigned(): boolean {
    return !!this.document?.manuallySignedPages?.length;
  }

  get mergeValues(): IMergeValues {
    const {
      value,
      name,
      sendDate,
      uniqueId,
      user,
      recipients,
      expirationDate,
    }: IDocument = this.document;

    return {
      document: {
        value,
        name,
        sendDate,
        uniqueId,
        expirationDate,
      },
      sender: user,
      recipients,
      entity: this.entity,
    };
  }

  handleClickLinkField(id: string) {
    this.$emit('clickLinkField', id);
  }

  handleClickInitialsField(id: string) {
    this.$emit('clickInitialsField', id);
  }

  handleClickSignField() {
    this.$emit('clickSignField');
  }

  handleVideoIntro() {
    this.$emit('videoIntro');
  }

  handlePoweredByClick() {
    window.open('https://www.getaccept.com/signed-getaccept-document');
  }

  distanceToMiddle(page: IPageElement): number {
    const position: number =
      page.element.offsetHeight / 2 + page.element.getBoundingClientRect().top;
    const middle: number = window.innerHeight / 2;
    return Math.abs(middle - position);
  }

  scrolledToTop(): boolean {
    return window.scrollY <= this.scrollMargin;
  }

  scrolledToBottom(): boolean {
    this.hasScrolledToBottom = true;
    return (
      document.documentElement.scrollHeight - window.scrollY - window.innerHeight <=
      this.scrollMargin
    );
  }

  calculatePagesInViewPort(pages: IPageElement[]): string[] {
    return pages
      .filter((page: IPageElement) => isPageInFocus(page))
      .map((page: IPageElement) => page.id);
  }

  calculateCurrentPages(pages: IPageElement[]) {
    if (!pages.length) {
      return;
    }
    const visiblePages: string[] = this.calculatePagesInViewPort(pages);
    this.handleCurrentPages(visiblePages);
  }

  handleCurrentPages(pageIds: string[]) {
    this.$emit('currentPages', pageIds);
  }

  handleUpdateField({ fieldId, value }: { fieldId: string; value: string }) {
    this.$emit('updateField', { fieldId, value });
  }

  handleTrackVideo(payload: { id: string; viewDuration: number }) {
    this.$emit('trackVideo', payload);
  }

  handleUploadAttachment(payload: { attachmentId: string; name: string }) {
    this.$emit('showUploadAttachmentModal', payload);
  }

  handleImageLoaded() {
    this.$emit('imageLoaded');
  }

  handleContentLinkClick(id: string) {
    this.$emit('contentLinkClick', id);
  }

  handleAttachmentView(id: string) {
    this.$emit('attachmentView', id);
  }

  debounceEmitDimensionsChanged: (element: HTMLElement) => void = debounce(
    this.dimensionsChanged,
    200
  );

  debounceCurrentDocumentPages: (pages: IPageElement[]) => void = debounce(
    this.calculateCurrentPages,
    200
  );

  dimensionsChanged(element: HTMLElement) {
    this.$emit('updatePageWidth', element.offsetWidth);
  }

  mounted() {
    this.elementResizeDetector.listenTo(this.$refs.contentBlockWrapper, (element: HTMLElement) => {
      this.debounceEmitDimensionsChanged(element);
      this.debounceCurrentDocumentPages(this.visibleDocumentContentPageElements);
    });

    window.addEventListener(
      'scroll',
      () => this.debounceCurrentDocumentPages(this.visibleDocumentContentPageElements),
      { passive: true }
    );
  }

  beforeDestroy() {
    this.elementResizeDetector.removeListener(this.$refs.contentBlockWrapper);
    window.removeEventListener('scroll', () =>
      this.debounceCurrentDocumentPages(this.visibleDocumentContentPageElements)
    );
  }
}
